import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { mobileAppsData, MobileAppItem } from "../pages/ourproducts/apps-products";
import { websiteData, WebsiteItem } from "../pages/ourproducts/website-products";
import { softwareData, SoftwareItem } from "../pages/ourproducts/software-products";
import { CARDS_DATA, PortfolioItem } from "../pages/Portfolio";

export interface FormSubmission {
  id: string;
  formType: "Contact Form" | "Meeting Booking" | "Quick Enquiry" | "Plan Application" | "Newsletter" | "Custom";
  fullName: string;
  email: string;
  phone?: string;
  company?: string;
  service?: string;
  budget?: string;
  notes?: string;
  selectedDate?: string;
  selectedTime?: string;
  planName?: string;
  status: "New" | "Contacted" | "In Progress" | "Closed";
  submittedAt: string;
  metadata?: Record<string, any>;
}

export interface ThemeConfig {
  primaryColor: string;
  accentColor: string;
  fontHeading: string;
  fontBody: string;
  borderRadius: string;
  siteName: string;
  siteLogoUrl: string;
  themePreset: "cyber-blue" | "emerald-green" | "violet-purple" | "sunset-orange" | "crimson-red" | "custom";
}

export interface PricingPlanItem {
  id: number;
  title: string;
  subTitle: string;
  category?: string; // "web" | "app" | "package" | custom category!
  inrPrice: string;
  usdPrice: string;
  billingCycle?: string; // "one-time" | "monthly" | "yearly" | custom
  link: string;
  features: string[];
  techStack?: string[];
  popular?: boolean;
  customBadge?: string;
  imageUrl?: string;
  buttonText?: string;
  isVisible?: boolean;
}

export interface HomeSectionConfig {
  id: string; // "hero" | "stats" | "services" | "portfolio" | "pricing" | "faq" | "cta" | "subscribe"
  name: string;
  isVisible: boolean;
  order: number;
  badge?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  ctaText?: string;
  ctaUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  customData?: any;
}

export interface HomeConfig {
  heroHeadline: string;
  heroSubheadline: string;
  heroPrimaryCtaText: string;
  heroPrimaryCtaUrl: string;
  heroSecondaryCtaText: string;
  heroSecondaryCtaUrl: string;
  announcementActive: boolean;
  announcementText: string;
  announcementUrl: string;
  statsClients: string;
  statsProjects: string;
  statsExperience: string;
  statsUptime: string;
}

export interface PageItem {
  id: string;
  name: string;
  path: string;
  title: string;
  description: string;
  isPublished: boolean;
  isIndexed: boolean;
  lastModified: string;
}

export interface MenuItem {
  id: string;
  name: string;
  path: string;
  iconName: string;
  isVisible: boolean;
  order: number;
  hasDropdown?: boolean;
  isCustom?: boolean;
}

export interface SmtpConfig {
  host: string;
  port: number;
  encryption: "TLS" | "SSL" | "NONE";
  user: string;
  pass: string;
  fromName: string;
  fromEmail: string;
  adminEmail: string;
  isActive: boolean;
}

export interface AdminUser {
  id: string;
  username: string;
  name: string;
  email: string;
  role: "Super Admin" | "Admin" | "Editor" | "Developer";
  status: "Active" | "Suspended";
  lastLogin?: string;
  createdAt: string;
}

export interface AdminContextType {
  // Authentication
  isAuthenticated: boolean;
  currentUser: AdminUser | null;
  login: (user: string, pass: string) => { success: boolean; message: string };
  logout: () => void;

  // Products
  apps: MobileAppItem[];
  websites: WebsiteItem[];
  software: SoftwareItem[];
  addProduct: (type: "apps" | "websites" | "software", product: any) => void;
  updateProduct: (type: "apps" | "websites" | "software", id: number, product: any) => void;
  deleteProduct: (type: "apps" | "websites" | "software", id: number) => void;
  toggleProductVisibility: (type: "apps" | "websites" | "software", id: number) => void;

  // Form Submissions & Leads
  submissions: FormSubmission[];
  isSyncingSubmissions: boolean;
  syncSubmissions: () => Promise<void>;
  testSmtpConnection: (cfg?: Partial<SmtpConfig>) => Promise<{ success: boolean; message: string; debugLog?: string[] }>;
  addSubmission: (submission: Omit<FormSubmission, "id" | "submittedAt" | "status"> & { status?: FormSubmission["status"] }) => void;
  updateSubmissionStatus: (id: string, status: FormSubmission["status"]) => void;
  deleteSubmission: (id: string) => void;
  clearSubmissions: () => void;

  // Site Styling & Theme
  themeConfig: ThemeConfig;
  updateThemeConfig: (updates: Partial<ThemeConfig>) => void;
  resetThemeConfig: () => void;

  // Portfolio
  portfolio: PortfolioItem[];
  portfolioCategories: string[];
  addPortfolio: (item: Omit<PortfolioItem, "id">) => void;
  updatePortfolio: (id: number, item: Partial<PortfolioItem>) => void;
  deletePortfolio: (id: number) => void;
  togglePortfolioVisibility: (id: number) => void;
  addPortfolioCategory: (cat: string) => void;
  deletePortfolioCategory: (cat: string) => void;

  // Pricing
  pricingPlans: PricingPlanItem[];
  pricingCategories: string[];
  webPlans: PricingPlanItem[];
  appPlans: PricingPlanItem[];
  packagePlans: PricingPlanItem[];
  addPricingPlan: (category: string, plan: Omit<PricingPlanItem, "id">) => void;
  updatePricingPlan: (arg1: any, arg2?: any, arg3?: any) => void;
  deletePricingPlan: (arg1: any, arg2?: any) => void;
  togglePricingVisibility: (id: number) => void;
  addPricingCategory: (cat: string) => void;
  deletePricingCategory: (cat: string) => void;

  // Home Page Config & Dynamic Sections
  homeConfig: HomeConfig;
  updateHomeConfig: (config: Partial<HomeConfig>) => void;
  homeSections: HomeSectionConfig[];
  updateHomeSections: (sections: HomeSectionConfig[]) => void;
  updateHomeSection: (id: string, updates: Partial<HomeSectionConfig>) => void;
  reorderHomeSection: (fromIndex: number, toIndex: number) => void;
  toggleHomeSectionVisibility: (id: string) => void;

  // Pages
  pages: PageItem[];
  updatePage: (id: string, updates: Partial<PageItem>) => void;
  addPage: (page: Omit<PageItem, "id">) => void;
  deletePage: (id: string) => void;

  // Custom Menu
  menuItems: MenuItem[];
  updateMenuItems: (items: MenuItem[]) => void;
  addMenuItem: (item: Omit<MenuItem, "id">) => void;
  deleteMenuItem: (id: string) => void;

  // SMTP
  smtpConfig: SmtpConfig;
  updateSmtpConfig: (config: Partial<SmtpConfig>) => void;

  // Users
  users: AdminUser[];
  addUser: (user: Omit<AdminUser, "id" | "createdAt">, password?: string) => void;
  updateUser: (id: string, updates: Partial<AdminUser>) => void;
  deleteUser: (id: string) => void;

  // Utilities
  resetDefaults: () => void;
  exportDatabase: () => string;
  importDatabase: (jsonString: string) => { success: boolean; message: string };
}

const STORAGE_KEY = "hickter_admin_database_v2";
const AUTH_KEY = "hickter_admin_session_v2";

const initialHomeConfig: HomeConfig = {
  heroHeadline: "Crafting High-Performance Digital Experiences & Next-Gen Software",
  heroSubheadline: "From enterprise custom web applications and native mobile apps to AI automation and omni-channel cloud solutions, Hickter scales businesses worldwide.",
  heroPrimaryCtaText: "Get Free Consultation",
  heroPrimaryCtaUrl: "/enquirynow",
  heroSecondaryCtaText: "Explore Products",
  heroSecondaryCtaUrl: "/ourproducts/our-products-all",
  announcementActive: true,
  announcementText: "🚀 Introducing eCom By Hickter – Real-Time Sync Storefront, App & Admin Platform!",
  announcementUrl: "/ourproducts/app/ecom",
  statsClients: "120+",
  statsProjects: "450+",
  statsExperience: "8+ Yrs",
  statsUptime: "99.98%"
};

const initialWebPlans: PricingPlanItem[] = [
  {
    id: 1,
    title: "Essential Web",
    subTitle: "WordPress / High-Speed Static",
    inrPrice: "9,999",
    usdPrice: "149",
    link: "https://hickter.com/plansapply/all-plans/website-development-standard-wordpress/",
    features: ["Custom Responsive Theme", "Mobile & SEO Friendly", "Contact Forms & Maps", "SSL & Domain Setup", "Fast Loading Speed"],
    techStack: ["WordPress", "HTML5/CSS3", "PHP"]
  },
  {
    id: 2,
    title: "Custom React Portal",
    subTitle: "Full Stack Web App",
    inrPrice: "24,999",
    usdPrice: "349",
    popular: true,
    link: "https://hickter.com/plansapply/all-plans/website-development-custom-react-web-app/",
    features: ["React.js Single Page App", "Dynamic API Integrations", "Custom Admin Dashboard", "High Performance Architecture", "Automated Backups"],
    techStack: ["React", "Node.js", "Tailwind"]
  },
  {
    id: 3,
    title: "E-Commerce Suite",
    subTitle: "Shopify / WooCommerce",
    inrPrice: "34,999",
    usdPrice: "499",
    link: "https://hickter.com/plansapply/all-plans/website-development-e-commerce-store/",
    features: ["Turnkey Store Setup", "App Store Integration", "Pro Shopify Templates", "Sales Analytics Panel", "24/7 Priority Support"],
    techStack: ["Shopify", "Liquid", "WooCommerce"]
  },
  {
    id: 4,
    title: "Enterprise Web",
    subTitle: "Next-Gen Cloud Stack",
    inrPrice: "50,000",
    usdPrice: "779",
    link: "https://hickter.com/plansapply/all-plans/website-development-advanced-react-next-js/",
    features: ["SSR & ISR Architecture", "Custom API Integration", "Scalable Database Setup", "Ultra Cloud Security", "Cloud-Native Ready"],
    techStack: ["Next.js", "TypeScript", "Node.js"]
  }
];

const initialAppPlans: PricingPlanItem[] = [
  {
    id: 1,
    title: "Native Android App",
    subTitle: "Single Platform Release",
    inrPrice: "39,999",
    usdPrice: "499",
    link: "/apply/app",
    features: ["Native Kotlin/Java Engine", "Material UI Design", "Push Notifications API", "Offline Data Caching", "Play Store Publishing"],
    techStack: ["Android", "Kotlin", "Firebase"]
  },
  {
    id: 2,
    title: "Cross-Platform Mobile App",
    subTitle: "iOS & Android Unified",
    inrPrice: "69,999",
    usdPrice: "899",
    popular: true,
    link: "/apply/app",
    features: ["Single React Native Codebase", "App Store & Play Store Ready", "Real-Time Chat/Sync API", "Payment Gateway Integration", "Biometric Authentication"],
    techStack: ["React Native", "TypeScript", "Node.js"]
  },
  {
    id: 3,
    title: "Enterprise Mobile Ecosystem",
    subTitle: "App + Backend + Admin",
    inrPrice: "1,19,999",
    usdPrice: "1,499",
    link: "/apply/app",
    features: ["Custom Cloud Architecture", "End-to-End Encryption", "Microservices Backend API", "Live GPS / Geofencing", "Dedicated SLA Maintenance"],
    techStack: ["React Native", "Node.js", "MongoDB", "AWS"]
  }
];

const initialPackagePlans: PricingPlanItem[] = [
  {
    id: 1,
    title: "Startup Digital Launchpack",
    subTitle: "Brand Identity + Responsive Website",
    inrPrice: "18,999",
    usdPrice: "249",
    link: "/apply/package",
    features: ["High-Conversion 5-Page Website", "Logo & Brand Identity Pack", "Domain, Hosting & SSL Included", "Google Analytics & Search Console", "Social Media Handles Setup"],
    techStack: ["React", "TailwindCSS", "SEO"]
  },
  {
    id: 2,
    title: "Growth Business Suite",
    subTitle: "Website + Mobile App + CRM",
    inrPrice: "59,999",
    usdPrice: "799",
    popular: true,
    link: "/apply/package",
    features: ["Full Responsive Website Portal", "Android Mobile App Release", "Integrated CRM Lead Pipeline", "Automated WhatsApp & Email Alerts", "Payment Gateway Setup"],
    techStack: ["React", "Android", "PHP", "MySQL"]
  },
  {
    id: 3,
    title: "Omni-Channel Enterprise",
    subTitle: "Complete Turnkey Platform",
    inrPrice: "1,49,999",
    usdPrice: "1,999",
    link: "/apply/package",
    features: ["Custom Cloud Web Application", "Cross-Platform iOS & Android Apps", "eCom Live Inventory Sync", "Dedicated Staff Admin Dashboard", "Priority 24/7 SLA Support"],
    techStack: ["Next.js", "React Native", "Node.js", "Cloud S3"]
  }
];

const initialPages: PageItem[] = [
  { id: "p1", name: "Home Page", path: "/", title: "Hickter | Innovative IT Solutions & Next-Gen Software", description: "Empowering businesses through cutting-edge web development, native apps, and enterprise platforms.", isPublished: true, isIndexed: true, lastModified: "2026-10-28" },
  { id: "p2", name: "About Us", path: "/about", title: "About Hickter | Our Mission & Engineering Team", description: "Discover Hickter's journey, technology leadership, and global software engineering capabilities.", isPublished: true, isIndexed: true, lastModified: "2026-10-25" },
  { id: "p3", name: "Services", path: "/services", title: "Professional Digital Services | Hickter", description: "Web development, mobile application development, digital marketing, enterprise software, and UI/UX.", isPublished: true, isIndexed: true, lastModified: "2026-10-20" },
  { id: "p4", name: "All Products", path: "/ourproducts/our-products-all", title: "Products Ecosystem | Hickter Deployments", description: "Explore CRMPro, POS WooCommerce, Storeit Cloud Vault, Tracknn, MusicPro, and eCom By Hickter.", isPublished: true, isIndexed: true, lastModified: "2026-10-28" },
  { id: "p5", name: "Portfolio", path: "/Portfolio", title: "Client Portfolio & Delivered Systems | Hickter", description: "Over 47+ live client projects across eCommerce, medical, corporate, real estate, and finance.", isPublished: true, isIndexed: true, lastModified: "2026-10-24" },
  { id: "p6", name: "Pricing & Plans", path: "/Pricing", title: "Transparent Pricing Plans | Hickter", description: "Clear pricing for websites, mobile apps, and complete turnkey digital packages.", isPublished: true, isIndexed: true, lastModified: "2026-10-26" },
  { id: "p7", name: "Meeting Booking", path: "/Meeting", title: "Schedule an Engineering Consultation | Hickter", description: "Book a 1-on-1 discovery meeting with Hickter lead architects.", isPublished: true, isIndexed: true, lastModified: "2026-10-15" },
  { id: "p8", name: "Contact Us", path: "/contact", title: "Get in Touch | 24x7 Global Support | Hickter", description: "Reach out for enquiries, custom project bids, and technical support.", isPublished: true, isIndexed: true, lastModified: "2026-10-18" },
  { id: "p9", name: "Quick Enquiry", path: "/enquirynow", title: "Direct Project Enquiry | Hickter", description: "Submit your requirements and download our official engineering brochure.", isPublished: true, isIndexed: true, lastModified: "2026-10-22" },
  { id: "p10", name: "Interactive Chat", path: "/chat", title: "Live AI Assistant & Project Bot | Hickter", description: "Get instant answers to technical questions, pricing queries, and service scopes.", isPublished: true, isIndexed: false, lastModified: "2026-10-12" }
];

const initialMenuItems: MenuItem[] = [
  { id: "m1", name: "Home", path: "/", iconName: "Home", isVisible: true, order: 1 },
  { id: "m2", name: "About", path: "/about", iconName: "Info", isVisible: true, order: 2 },
  { id: "m3", name: "Products", path: "/ourproducts/our-products-all", iconName: "Boxes", isVisible: true, order: 3 },
  { id: "m4", name: "Services", path: "/services", iconName: "Layers", isVisible: true, order: 4, hasDropdown: true },
  { id: "m5", name: "Portfolio", path: "/Portfolio", iconName: "Briefcase", isVisible: true, order: 5 },
  { id: "m6", name: "Pricing", path: "/Pricing", iconName: "CreditCard", isVisible: true, order: 6 },
  { id: "m7", name: "Meeting", path: "/Meeting", iconName: "Calendar", isVisible: true, order: 7 },
  { id: "m8", name: "Contact", path: "/contact", iconName: "Send", isVisible: true, order: 8 }
];

const initialSmtpConfig: SmtpConfig = {
  host: "s802.bom1.mysecurecloudhost.com",
  port: 465,
  encryption: "SSL",
  user: "admin@hickter.com",
  pass: "contact@007",
  fromName: "Hickter Admin Portal",
  fromEmail: "admin@hickter.com",
  adminEmail: "admin@hickter.com",
  isActive: true
};

const initialUsers: AdminUser[] = [
  {
    id: "u1",
    username: "superadmin",
    name: "Master Super Admin",
    email: "admin@hickter.com",
    role: "Super Admin",
    status: "Active",
    lastLogin: "Just now",
    createdAt: "2026-01-01"
  },
  {
    id: "u2",
    username: "devlead",
    name: "Lead System Architect",
    email: "lead@hickter.com",
    role: "Admin",
    status: "Active",
    lastLogin: "2 days ago",
    createdAt: "2026-03-15"
  },
  {
    id: "u3",
    username: "contenteditor",
    name: "Editorial Content Manager",
    email: "editor@hickter.com",
    role: "Editor",
    status: "Active",
    lastLogin: "1 week ago",
    createdAt: "2026-05-10"
  }
];

const initialSubmissions: FormSubmission[] = [
  {
    id: "sub_1",
    formType: "Contact Form",
    fullName: "Aarav Sharma",
    email: "aarav.sharma@fintechgrowth.io",
    phone: "+91 98201 44521",
    company: "Fintech Growth Hub",
    service: "Custom Enterprise Software",
    budget: "$10,000 - $25,000",
    notes: "Looking to develop a multi-tenant cloud banking reconciliation portal with automated reports and WhatsApp notification triggers.",
    status: "New",
    submittedAt: "2026-10-29 11:30 AM"
  },
  {
    id: "sub_2",
    formType: "Meeting Booking",
    fullName: "Elena Rostova",
    email: "elena@nordicvoyages.com",
    phone: "+44 7700 900142",
    company: "Nordic Voyages Ltd",
    service: "Mobile App Development",
    selectedDate: "2026-11-04",
    selectedTime: "03:30 PM",
    notes: "Need native iOS & Android travel booking app with real-time flight tracking and multi-currency Stripe integration.",
    status: "Contacted",
    submittedAt: "2026-10-28 04:15 PM"
  },
  {
    id: "sub_3",
    formType: "Quick Enquiry",
    fullName: "Vikram Malhotra",
    email: "vikram@omnilogistics.in",
    phone: "+91 94123 78901",
    company: "Omni Logistics India",
    service: "Real-Time Sync E-Commerce",
    budget: "₹2,50,000 - ₹5,00,000",
    notes: "Interested in eCom By Hickter for our warehouse operations. Need real-time inventory sync between mobile apps and web store.",
    status: "New",
    submittedAt: "2026-10-27 02:40 PM"
  },
  {
    id: "sub_4",
    formType: "Plan Application",
    fullName: "David Chen",
    email: "david@luminaai.tech",
    phone: "+1 (415) 555-0199",
    company: "Lumina AI Labs",
    service: "Full Stack Portal (Next.js)",
    planName: "Enterprise Web Plan ($779)",
    budget: "$779 / project",
    notes: "Ready to proceed with Next.js web portal development. Please provide scope doc and payment details.",
    status: "In Progress",
    submittedAt: "2026-10-26 09:20 AM"
  }
];

const initialThemeConfig: ThemeConfig = {
  primaryColor: "#2563eb",
  accentColor: "#06b6d4",
  fontHeading: "'Space Grotesk', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  borderRadius: "0.75rem",
  siteName: "Hickter | Technology & Solutions",
  siteLogoUrl: "/assets/logo/dl.webp",
  themePreset: "cyber-blue"
};

const initialPortfolioCategories: string[] = [
  "wordpress",
  "shopify",
  "woocommerce",
  "react",
  "php",
  "apps",
  "enterprise",
  "ai-ml"
];

const initialPricingCategories: string[] = ["web", "app", "package"];

const initialHomeSections: HomeSectionConfig[] = [
  {
    id: "hero",
    name: "Hero Banner & Dynamic Headlines",
    isVisible: true,
    order: 1,
    badge: "Official Hickter Platform 2026",
    title: "Engineering Scalable Digital Experiences",
    subtitle: "From enterprise custom web applications and native mobile apps to AI automation and omni-channel cloud solutions, Hickter scales businesses worldwide.",
    ctaText: "Get Free Consultation",
    ctaUrl: "/enquirynow",
    secondaryCtaText: "Explore Products",
    secondaryCtaUrl: "/ourproducts/our-products-all"
  },
  {
    id: "stats",
    name: "Stats & Trust Metrics Bar",
    isVisible: true,
    order: 2,
    badge: "Proven Track Record",
    title: "Delivering Excellence at Global Scale",
    subtitle: "Real numbers backed by hundreds of satisfied clients and scalable cloud software deployments."
  },
  {
    id: "services",
    name: "Services Ecosystem",
    isVisible: true,
    order: 3,
    badge: "Engineering Capabilities",
    title: "High-Performance Digital Solutions",
    subtitle: "End-to-end software development and growth strategies tailored for startups, SMBs, and enterprise brands."
  },
  {
    id: "portfolio",
    name: "Featured Client Projects Carousel",
    isVisible: true,
    order: 4,
    badge: "Client Showcase",
    title: "Featured Client Projects",
    subtitle: "Explore real-world client deployments across web platforms, e-commerce stores, and mobile applications.",
    ctaText: "View All 300+ Projects",
    ctaUrl: "/Portfolio"
  },
  {
    id: "pricing",
    name: "Pricing & Plans Showcase",
    isVisible: true,
    order: 5,
    badge: "Transparent Investment",
    title: "Predictable & Scalable Pricing Plans",
    subtitle: "Choose from our turnkey web, mobile app, and full-stack digital development packages.",
    ctaText: "View All Packages",
    ctaUrl: "/Pricing"
  },
  {
    id: "faq",
    name: "Frequently Asked Questions",
    isVisible: true,
    order: 6,
    badge: "Got Questions?",
    title: "Frequently Asked Questions",
    subtitle: "Everything you need to know about our development process, milestone delivery, and ongoing support."
  },
  {
    id: "cta",
    name: "Ready to Scale CTA Banner",
    isVisible: true,
    order: 7,
    badge: "Start Your Project Today",
    title: "Ready to Scale Your Business Online?",
    subtitle: "Get in touch with our engineering and growth team for high-performance websites, custom apps, and digital marketing strategies.",
    ctaText: "Get Custom Proposal",
    ctaUrl: "/enquirynow",
    secondaryCtaText: "Schedule Consultation",
    secondaryCtaUrl: "/Meeting"
  },
  {
    id: "subscribe",
    name: "Newsletter & Instant App Demo",
    isVisible: true,
    order: 8,
    badge: "Stay Ahead",
    title: "Unlock Instant Live Mobile & Web Demos",
    subtitle: "Subscribe to receive direct access to our latest APK builds, private repos, and architecture blueprints.",
    ctaText: "Get Instant Demo",
    ctaUrl: "/chat"
  }
];

const initialPricingPlans: PricingPlanItem[] = [
  ...initialWebPlans.map(p => ({ ...p, category: "web", isVisible: true })),
  ...initialAppPlans.map(p => ({ ...p, category: "app", isVisible: true })),
  ...initialPackagePlans.map(p => ({ ...p, category: "package", isVisible: true }))
];

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load persisted database or initialize defaults
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        let pricingPlans = parsed.pricingPlans;
        if (!pricingPlans || pricingPlans.length === 0) {
          pricingPlans = [
            ...(parsed.webPlans || initialWebPlans).map((p: any) => ({ ...p, category: "web", isVisible: p.isVisible !== false })),
            ...(parsed.appPlans || initialAppPlans).map((p: any) => ({ ...p, category: "app", isVisible: p.isVisible !== false })),
            ...(parsed.packagePlans || initialPackagePlans).map((p: any) => ({ ...p, category: "package", isVisible: p.isVisible !== false }))
          ];
        }
        let currentSmtp = parsed.smtpConfig;
        if (!currentSmtp || currentSmtp.host === "smtp.mailgun.org" || currentSmtp.pass === "hickter_smtp_secret_token" || currentSmtp.user === "postmaster@mg.hickter.com") {
          currentSmtp = initialSmtpConfig;
        }

        return {
          ...parsed,
          smtpConfig: currentSmtp,
          submissions: parsed.submissions || initialSubmissions,
          themeConfig: parsed.themeConfig || initialThemeConfig,
          homeSections: parsed.homeSections || initialHomeSections,
          portfolioCategories: parsed.portfolioCategories || initialPortfolioCategories,
          pricingCategories: parsed.pricingCategories || initialPricingCategories,
          pricingPlans: pricingPlans
        };
      }
    } catch (e) {
      console.error("Failed to parse saved admin data:", e);
    }
    return {
      apps: mobileAppsData,
      websites: websiteData,
      software: softwareData,
      portfolio: CARDS_DATA,
      webPlans: initialWebPlans,
      appPlans: initialAppPlans,
      packagePlans: initialPackagePlans,
      pricingPlans: initialPricingPlans,
      pricingCategories: initialPricingCategories,
      portfolioCategories: initialPortfolioCategories,
      homeSections: initialHomeSections,
      homeConfig: initialHomeConfig,
      pages: initialPages,
      menuItems: initialMenuItems,
      smtpConfig: initialSmtpConfig,
      users: initialUsers,
      submissions: initialSubmissions,
      themeConfig: initialThemeConfig
    };
  });

  // Auth session
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(() => {
    try {
      const session = localStorage.getItem(AUTH_KEY);
      if (session) {
        return JSON.parse(session);
      }
    } catch (e) {
      console.error("Failed to parse session:", e);
    }
    return null;
  });

  // Save changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error("Failed to persist admin data:", e);
    }
  }, [data]);

  const [isSyncingSubmissions, setIsSyncingSubmissions] = useState(false);

  // Sync Submissions from Server Storage
  const syncSubmissions = useCallback(async () => {
    setIsSyncingSubmissions(true);
    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const endpoint = isLocal ? "http://localhost:5000/api/submissions" : "/api/submissions.php?action=list";
      const res = await fetch(endpoint);
      const resData = await res.json();
      if (resData && resData.success && Array.isArray(resData.submissions)) {
        setData((prev: any) => {
          const localList: FormSubmission[] = prev.submissions || [];
          const remoteList: any[] = resData.submissions;

          const map = new Map<string, FormSubmission>();
          remoteList.forEach((r) => {
            map.set(r.id, {
              id: r.id,
              formType: r.formType || "Contact Form",
              fullName: r.fullName || "Client",
              email: r.email,
              phone: r.phone,
              company: r.company,
              service: r.service,
              planName: r.planName,
              budget: r.budget,
              selectedDate: r.selectedDate,
              selectedTime: r.selectedTime,
              notes: r.notes || r.message,
              status: r.status || "New",
              submittedAt: r.submittedAt || new Date().toISOString()
            });
          });

          localList.forEach((l) => {
            if (!map.has(l.id)) {
              map.set(l.id, l);
            }
          });

          const merged = Array.from(map.values()).sort((a, b) => {
            const timeA = new Date(a.submittedAt).getTime() || 0;
            const timeB = new Date(b.submittedAt).getTime() || 0;
            return timeB - timeA;
          });

          return {
            ...prev,
            submissions: merged
          };
        });
      }
    } catch (err) {
      console.warn("Could not sync remote submissions:", err);
    } finally {
      setIsSyncingSubmissions(false);
    }
  }, []);

  // Poll submissions on mount and every 30 seconds
  useEffect(() => {
    syncSubmissions();
    const interval = setInterval(() => {
      syncSubmissions();
    }, 30000);
    return () => clearInterval(interval);
  }, [syncSubmissions]);

  // Dynamically apply site styling (Primary Color, Accent, Fonts, Border Radius)
  useEffect(() => {
    if (data?.themeConfig) {
      const cfg = data.themeConfig;
      if (cfg.primaryColor) {
        document.documentElement.style.setProperty("--brand-primary", cfg.primaryColor);
      }
      if (cfg.accentColor) {
        document.documentElement.style.setProperty("--brand-accent", cfg.accentColor);
      }
      if (cfg.fontHeading) {
        document.documentElement.style.setProperty("--font-heading", cfg.fontHeading);
      }
      if (cfg.fontBody) {
        document.documentElement.style.setProperty("--font-body", cfg.fontBody);
      }
      if (cfg.borderRadius) {
        document.documentElement.style.setProperty("--border-radius-base", cfg.borderRadius);
      }
    }
  }, [data?.themeConfig]);

  // Save auth session to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(currentUser));
    } else {
      localStorage.removeItem(AUTH_KEY);
    }
  }, [currentUser]);

  // Auth Functions
  const login = (user: string, pass: string) => {
    const cleanUser = user.trim().toLowerCase();
    const cleanPass = pass.trim();

    // Check superadmin master credentials
    if (cleanUser === "superadmin" && cleanPass === "admin@007") {
      const adminObj: AdminUser = {
        id: "u1",
        username: "superadmin",
        name: "Master Super Admin",
        email: "admin@hickter.com",
        role: "Super Admin",
        status: "Active",
        lastLogin: new Date().toLocaleTimeString(),
        createdAt: "2026-01-01"
      };
      setCurrentUser(adminObj);
      return { success: true, message: "Welcome back, Super Admin!" };
    }

    // Check other registered users
    const foundUser = data.users.find(
      (u: AdminUser) => u.username.toLowerCase() === cleanUser && u.status === "Active"
    );

    if (foundUser && cleanPass === "admin@007") {
      const updatedUser = { ...foundUser, lastLogin: new Date().toLocaleTimeString() };
      setCurrentUser(updatedUser);
      return { success: true, message: `Welcome back, ${foundUser.name}!` };
    }

    return { success: false, message: "Invalid username or password. Please verify credentials." };
  };

  const logout = () => {
    setCurrentUser(null);
  };

  // Product Methods
  const addProduct = (type: "apps" | "websites" | "software", product: any) => {
    setData((prev: any) => {
      const list = prev[type] || [];
      const newId = list.length > 0 ? Math.max(...list.map((i: any) => i.id)) + 1 : 1;
      const newProduct = { ...product, id: newId };
      return { ...prev, [type]: [newProduct, ...list] };
    });
  };

  const updateProduct = (type: "apps" | "websites" | "software", id: number, product: any) => {
    setData((prev: any) => {
      const list = prev[type] || [];
      const updated = list.map((item: any) => (item.id === id ? { ...item, ...product } : item));
      return { ...prev, [type]: updated };
    });
  };

  const deleteProduct = (type: "apps" | "websites" | "software", id: number) => {
    setData((prev: any) => {
      const list = prev[type] || [];
      return { ...prev, [type]: list.filter((item: any) => item.id !== id) };
    });
  };

  const toggleProductVisibility = (type: "apps" | "websites" | "software", id: number) => {
    setData((prev: any) => {
      const list = prev[type] || [];
      const updated = list.map((item: any) =>
        item.id === id ? { ...item, isVisible: item.isVisible === false ? true : false } : item
      );
      return { ...prev, [type]: updated };
    });
  };

  // Portfolio Methods
  const addPortfolio = (item: Omit<PortfolioItem, "id">) => {
    setData((prev: any) => {
      const list = prev.portfolio || [];
      const newId = list.length > 0 ? Math.max(...list.map((i: any) => i.id)) + 1 : 1;
      const newItem = { ...item, id: newId, isVisible: item.isVisible !== false };
      return { ...prev, portfolio: [newItem, ...list] };
    });
  };

  const updatePortfolio = (id: number, item: Partial<PortfolioItem>) => {
    setData((prev: any) => {
      const list = prev.portfolio || [];
      const updated = list.map((p: any) => (p.id === id ? { ...p, ...item } : p));
      return { ...prev, portfolio: updated };
    });
  };

  const deletePortfolio = (id: number) => {
    setData((prev: any) => {
      const list = prev.portfolio || [];
      return { ...prev, portfolio: list.filter((p: any) => p.id !== id) };
    });
  };

  const togglePortfolioVisibility = (id: number) => {
    setData((prev: any) => {
      const list = prev.portfolio || [];
      const updated = list.map((p: any) =>
        p.id === id ? { ...p, isVisible: p.isVisible === false ? true : false } : p
      );
      return { ...prev, portfolio: updated };
    });
  };

  const addPortfolioCategory = (cat: string) => {
    const clean = cat.trim().toLowerCase();
    if (!clean) return;
    setData((prev: any) => {
      const cats = prev.portfolioCategories || initialPortfolioCategories;
      if (cats.includes(clean)) return prev;
      return { ...prev, portfolioCategories: [...cats, clean] };
    });
  };

  const deletePortfolioCategory = (cat: string) => {
    const clean = cat.trim().toLowerCase();
    setData((prev: any) => {
      const cats = prev.portfolioCategories || initialPortfolioCategories;
      return { ...prev, portfolioCategories: cats.filter((c: string) => c !== clean) };
    });
  };

  // Pricing Methods
  const addPricingPlan = (category: string, plan: Omit<PricingPlanItem, "id">) => {
    setData((prev: any) => {
      const list = prev.pricingPlans || initialPricingPlans;
      const newId = list.length > 0 ? Math.max(...list.map((i: any) => i.id)) + 1 : 1;
      const newPlan = {
        ...plan,
        category: category || plan.category || "web",
        id: newId,
        isVisible: plan.isVisible !== false
      };
      return { ...prev, pricingPlans: [...list, newPlan] };
    });
  };

  const updatePricingPlan = (arg1: any, arg2?: any, arg3?: any) => {
    setData((prev: any) => {
      const list = prev.pricingPlans || initialPricingPlans;
      let id: number;
      let updates: Partial<PricingPlanItem>;
      if (typeof arg1 === "string" && typeof arg2 === "number") {
        id = arg2;
        updates = { ...arg3, category: arg1 };
      } else {
        id = Number(arg1);
        updates = arg2 || {};
      }
      const updated = list.map((item: any) => (item.id === id ? { ...item, ...updates } : item));
      return { ...prev, pricingPlans: updated };
    });
  };

  const deletePricingPlan = (arg1: any, arg2?: any) => {
    const id = typeof arg1 === "number" ? arg1 : Number(arg2);
    setData((prev: any) => {
      const list = prev.pricingPlans || initialPricingPlans;
      return { ...prev, pricingPlans: list.filter((item: any) => item.id !== id) };
    });
  };

  const togglePricingVisibility = (id: number) => {
    setData((prev: any) => {
      const list = prev.pricingPlans || initialPricingPlans;
      const updated = list.map((item: any) =>
        item.id === id ? { ...item, isVisible: item.isVisible === false ? true : false } : item
      );
      return { ...prev, pricingPlans: updated };
    });
  };

  const addPricingCategory = (cat: string) => {
    const clean = cat.trim().toLowerCase();
    if (!clean) return;
    setData((prev: any) => {
      const cats = prev.pricingCategories || initialPricingCategories;
      if (cats.includes(clean)) return prev;
      return { ...prev, pricingCategories: [...cats, clean] };
    });
  };

  const deletePricingCategory = (cat: string) => {
    const clean = cat.trim().toLowerCase();
    setData((prev: any) => {
      const cats = prev.pricingCategories || initialPricingCategories;
      return { ...prev, pricingCategories: cats.filter((c: string) => c !== clean) };
    });
  };

  // Dynamic Home Sections Methods
  const updateHomeSections = (sections: HomeSectionConfig[]) => {
    setData((prev: any) => ({
      ...prev,
      homeSections: sections
    }));
  };

  const updateHomeSection = (id: string, updates: Partial<HomeSectionConfig>) => {
    setData((prev: any) => ({
      ...prev,
      homeSections: (prev.homeSections || initialHomeSections).map((s: HomeSectionConfig) =>
        s.id === id ? { ...s, ...updates } : s
      )
    }));
  };

  const reorderHomeSection = (fromIndex: number, toIndex: number) => {
    setData((prev: any) => {
      const list = [...(prev.homeSections || initialHomeSections)];
      if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) {
        return prev;
      }
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      const reordered = list.map((item, idx) => ({ ...item, order: idx + 1 }));
      return { ...prev, homeSections: reordered };
    });
  };

  const toggleHomeSectionVisibility = (id: string) => {
    setData((prev: any) => ({
      ...prev,
      homeSections: (prev.homeSections || initialHomeSections).map((s: HomeSectionConfig) =>
        s.id === id ? { ...s, isVisible: !s.isVisible } : s
      )
    }));
  };

  // Home Config
  const updateHomeConfig = (config: Partial<HomeConfig>) => {
    setData((prev: any) => ({
      ...prev,
      homeConfig: { ...prev.homeConfig, ...config }
    }));
  };

  // Page Methods
  const updatePage = (id: string, updates: Partial<PageItem>) => {
    setData((prev: any) => ({
      ...prev,
      pages: prev.pages.map((p: PageItem) =>
        p.id === id ? { ...p, ...updates, lastModified: new Date().toISOString().split("T")[0] } : p
      )
    }));
  };

  const addPage = (page: Omit<PageItem, "id">) => {
    setData((prev: any) => ({
      ...prev,
      pages: [
        ...prev.pages,
        {
          ...page,
          id: `p_${Date.now()}`,
          lastModified: new Date().toISOString().split("T")[0]
        }
      ]
    }));
  };

  const deletePage = (id: string) => {
    setData((prev: any) => ({
      ...prev,
      pages: prev.pages.filter((p: PageItem) => p.id !== id)
    }));
  };

  // Menu Methods
  const updateMenuItems = (items: MenuItem[]) => {
    setData((prev: any) => ({
      ...prev,
      menuItems: items
    }));
  };

  const addMenuItem = (item: Omit<MenuItem, "id">) => {
    setData((prev: any) => ({
      ...prev,
      menuItems: [...prev.menuItems, { ...item, id: `m_${Date.now()}` }]
    }));
  };

  const deleteMenuItem = (id: string) => {
    setData((prev: any) => ({
      ...prev,
      menuItems: prev.menuItems.filter((m: MenuItem) => m.id !== id)
    }));
  };

  // SMTP Method
  const updateSmtpConfig = (config: Partial<SmtpConfig>) => {
    setData((prev: any) => ({
      ...prev,
      smtpConfig: { ...prev.smtpConfig, ...config }
    }));
  };

  // User Methods
  const addUser = (user: Omit<AdminUser, "id" | "createdAt">) => {
    setData((prev: any) => ({
      ...prev,
      users: [
        ...prev.users,
        {
          ...user,
          id: `u_${Date.now()}`,
          createdAt: new Date().toISOString().split("T")[0]
        }
      ]
    }));
  };

  const updateUser = (id: string, updates: Partial<AdminUser>) => {
    setData((prev: any) => ({
      ...prev,
      users: prev.users.map((u: AdminUser) => (u.id === id ? { ...u, ...updates } : u))
    }));
  };

  const deleteUser = (id: string) => {
    setData((prev: any) => ({
      ...prev,
      users: prev.users.filter((u: AdminUser) => u.id !== id)
    }));
  };

  // Form Submissions & Leads Methods
  const addSubmission = (sub: Omit<FormSubmission, "id" | "submittedAt" | "status"> & { status?: FormSubmission["status"] }) => {
    const newSubmission: FormSubmission = {
      ...sub,
      id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      status: sub.status || "New",
      submittedAt: new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    };
    setData((prev: any) => ({
      ...prev,
      submissions: [newSubmission, ...(prev.submissions || [])]
    }));
  };

  const updateSubmissionStatus = (id: string, status: FormSubmission["status"]) => {
    setData((prev: any) => ({
      ...prev,
      submissions: (prev.submissions || []).map((s: FormSubmission) =>
        s.id === id ? { ...s, status } : s
      )
    }));

    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const endpoint = isLocal ? "http://localhost:5000/api/submissions/status" : "/api/submissions.php?action=update_status";
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status })
      }).catch(() => {});
    } catch (e) {}
  };

  const deleteSubmission = (id: string) => {
    setData((prev: any) => ({
      ...prev,
      submissions: (prev.submissions || []).filter((s: FormSubmission) => s.id !== id)
    }));

    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const endpoint = isLocal ? "http://localhost:5000/api/submissions/delete" : "/api/submissions.php?action=delete";
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      }).catch(() => {});
    } catch (e) {}
  };

  const clearSubmissions = () => {
    setData((prev: any) => ({
      ...prev,
      submissions: []
    }));

    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const endpoint = isLocal ? "http://localhost:5000/api/submissions/clear" : "/api/submissions.php?action=clear";
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      }).catch(() => {});
    } catch (e) {}
  };

  const testSmtpConnection = async (customConfig?: Partial<SmtpConfig>): Promise<{ success: boolean; message: string; debugLog?: string[] }> => {
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const endpoint = isLocal ? "http://localhost:5000/api/test-smtp" : "/api/submissions.php?action=test_smtp";
    const targetConfig = { ...data.smtpConfig, ...customConfig };
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: targetConfig.host,
          port: targetConfig.port,
          user: targetConfig.user,
          pass: targetConfig.pass,
          to: targetConfig.adminEmail || "admin@hickter.com"
        })
      });
      const resData = await response.json();
      return resData;
    } catch (err: any) {
      return { success: false, message: "Network connection to SMTP test endpoint failed: " + err.message };
    }
  };

  // Theme & Styling Customizer Methods
  const updateThemeConfig = (updates: Partial<ThemeConfig>) => {
    setData((prev: any) => ({
      ...prev,
      themeConfig: { ...prev.themeConfig, ...updates }
    }));
  };

  const resetThemeConfig = () => {
    setData((prev: any) => ({
      ...prev,
      themeConfig: initialThemeConfig
    }));
  };

  // Reset & Backup Utilities
  const resetDefaults = () => {
    const defaults = {
      apps: mobileAppsData,
      websites: websiteData,
      software: softwareData,
      portfolio: CARDS_DATA,
      webPlans: initialWebPlans,
      appPlans: initialAppPlans,
      packagePlans: initialPackagePlans,
      pricingPlans: initialPricingPlans,
      pricingCategories: initialPricingCategories,
      portfolioCategories: initialPortfolioCategories,
      homeSections: initialHomeSections,
      homeConfig: initialHomeConfig,
      pages: initialPages,
      menuItems: initialMenuItems,
      smtpConfig: initialSmtpConfig,
      users: initialUsers,
      submissions: initialSubmissions,
      themeConfig: initialThemeConfig
    };
    setData(defaults);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
  };

  const exportDatabase = () => {
    return JSON.stringify(data, null, 2);
  };

  const importDatabase = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (!parsed.apps || !parsed.portfolio || !parsed.homeConfig) {
        return { success: false, message: "Invalid schema format. Missing critical store fields." };
      }
      setData(parsed);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      return { success: true, message: "Site database successfully restored and synchronized!" };
    } catch (e: any) {
      return { success: false, message: `Failed to import JSON: ${e.message}` };
    }
  };

  const currentPricingPlans = data.pricingPlans || initialPricingPlans;

  return (
    <AdminContext.Provider
      value={{
        isAuthenticated: !!currentUser,
        currentUser,
        login,
        logout,
        apps: data.apps || [],
        websites: data.websites || [],
        software: data.software || [],
        addProduct,
        updateProduct,
        deleteProduct,
        toggleProductVisibility,
        submissions: data.submissions || initialSubmissions,
        isSyncingSubmissions,
        syncSubmissions,
        testSmtpConnection,
        addSubmission,
        updateSubmissionStatus,
        deleteSubmission,
        clearSubmissions,
        themeConfig: data.themeConfig || initialThemeConfig,
        updateThemeConfig,
        resetThemeConfig,
        portfolio: data.portfolio || [],
        portfolioCategories: data.portfolioCategories || initialPortfolioCategories,
        addPortfolio,
        updatePortfolio,
        deletePortfolio,
        togglePortfolioVisibility,
        addPortfolioCategory,
        deletePortfolioCategory,
        pricingPlans: currentPricingPlans,
        pricingCategories: data.pricingCategories || initialPricingCategories,
        webPlans: currentPricingPlans.filter((p: any) => (p.category || "web") === "web"),
        appPlans: currentPricingPlans.filter((p: any) => p.category === "app"),
        packagePlans: currentPricingPlans.filter((p: any) => p.category === "package"),
        addPricingPlan,
        updatePricingPlan,
        deletePricingPlan,
        togglePricingVisibility,
        addPricingCategory,
        deletePricingCategory,
        homeConfig: data.homeConfig || initialHomeConfig,
        updateHomeConfig,
        homeSections: data.homeSections || initialHomeSections,
        updateHomeSections,
        updateHomeSection,
        reorderHomeSection,
        toggleHomeSectionVisibility,
        pages: data.pages || initialPages,
        updatePage,
        addPage,
        deletePage,
        menuItems: data.menuItems || initialMenuItems,
        updateMenuItems,
        addMenuItem,
        deleteMenuItem,
        smtpConfig: data.smtpConfig || initialSmtpConfig,
        updateSmtpConfig,
        users: data.users || initialUsers,
        addUser,
        updateUser,
        deleteUser,
        resetDefaults,
        exportDatabase,
        importDatabase
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};
